import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { sendOtp, verifyOtp, getMsg91Config, msg91VerifyAccessToken } from "@/lib/userAuth";
import { toast } from "sonner";

const MSG91_SCRIPT = "https://verify.msg91.com/otp-provider.js";

/**
 * Phone OTP verification.
 *
 * Behavior:
 *  - If backend `/api/config/msg91` returns a widgetId + tokenAuth, we load the MSG91
 *    OTP Widget SDK and use `window.sendOtp` / `window.verifyOtp` / `window.retryOtp`
 *    with `exposeMethods: true`. On successful verify we send MSG91's access-token to
 *    the backend which verifies it via MSG91's server API and issues our JWT.
 *  - Otherwise we fall back to a local dev OTP shown on-screen (works out of the box).
 */
export const PhoneOtpVerify = ({ phone, onPhoneChange, verified, onVerified, name }) => {
  const [step, setStep] = useState("phone"); // phone | otp
  const [devOtp, setDevOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [reqId, setReqId] = useState(null);
  const [sendDebug, setSendDebug] = useState(null); // {ok:bool, payload:any}
  const [verifyDebug, setVerifyDebug] = useState(null); // {ok:bool, payload:any}

  // MSG91 widget state
  const [widgetCfg, setWidgetCfg] = useState(null); // {widgetId, tokenAuth} | null
  const scriptReady = useRef(false);
  const cfgFetched = useRef(false);

  useEffect(() => {
    if (cfgFetched.current) return;
    cfgFetched.current = true;
    getMsg91Config()
      .then((cfg) => {
        if (cfg?.widgetId && cfg?.tokenAuth) setWidgetCfg(cfg);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!widgetCfg) return;
    // Inject script once
    if (document.querySelector(`script[src="${MSG91_SCRIPT}"]`)) {
      scriptReady.current = true;
      initWidget();
      return;
    }
    const s = document.createElement("script");
    s.src = MSG91_SCRIPT;
    s.async = true;
    s.onload = () => { scriptReady.current = true; initWidget(); };
    s.onerror = () => toast.error("Could not load MSG91 widget — using local OTP");
    document.body.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetCfg]);

  const initWidget = () => {
    if (!window.initSendOTP || !widgetCfg) return;
    try {
      window.initSendOTP({
        widgetId: widgetCfg.widgetId,
        tokenAuth: widgetCfg.tokenAuth,
        exposeMethods: true,
        success: () => {},
        failure: (err) => console.error("MSG91 widget error:", err),
      });
    } catch (e) {
      console.error("MSG91 initSendOTP failed:", e);
    }
  };

  if (verified) {
    return (
      <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="phone-verified-badge">
        <span className="text-sm text-emerald-700 font-semibold">+91 {phone} <span className="text-xs text-emerald-600">✓ verified</span></span>
      </div>
    );
  }

  const doSend = async () => {
    const clean = (phone || "").replace(/\D/g, "");
    if (clean.length < 10) { toast.error("Enter a valid 10-digit phone number"); return; }
    setSending(true);
    setSendDebug(null);
    setVerifyDebug(null);
    setOtp("");
    setReqId(null);
    try {
      if (widgetCfg && scriptReady.current && window.sendOtp) {
        // MSG91 widget flow — identifier is countrycode+number (no +)
        const identifier = "91" + clean;
        console.log("[MSG91] sendOtp called with identifier:", identifier);
        window.sendOtp(
          identifier,
          (data) => {
            console.log("[MSG91] sendOtp SUCCESS payload:", data);
            setSendDebug({ ok: true, payload: data });
            setReqId(data?.reqId ?? data?.request_id ?? data?.data?.reqId ?? null);
            setDevOtp("");
            setStep("otp");
            setSending(false);
            toast.success("MSG91 accepted the request — check your SMS");
          },
          (err) => {
            console.error("[MSG91] sendOtp FAILURE payload:", err);
            setSendDebug({ ok: false, payload: err });
            setSending(false);
            toast.error(err?.message || err?.msg || "MSG91 rejected the send request — see details below");
          }
        );
        return;
      }
      // Widget script not loaded (widget config missing). Do NOT display any fake OTP.
      toast.error("MSG91 widget failed to load. Reload the page.");
      setSendDebug({ ok: false, payload: { reason: "widget_not_loaded" } });
      setSending(false);
    } catch (e) {
      console.error("doSend exception:", e);
      setSendDebug({ ok: false, payload: String(e) });
      toast.error("Send OTP failed");
      setSending(false);
    }
  };

  const doResend = () => {
    if (!(widgetCfg && window.retryOtp && reqId)) { doSend(); return; }
    window.retryOtp(
      "11", // channel 11 = SMS
      () => toast.success("OTP resent"),
      (err) => toast.error(err?.message || "Could not resend"),
      reqId
    );
  };

  const doVerify = async () => {
    if (otp.length < 4) { toast.error("Enter the OTP"); return; }
    setVerifying(true);
    setVerifyDebug(null);
    const clean = (phone || "").replace(/\D/g, "");
    try {
      if (!(widgetCfg && scriptReady.current && window.verifyOtp)) {
        setVerifyDebug({ ok: false, payload: { reason: "widget_not_ready" } });
        toast.error("Verification widget not ready. Reload the page.");
        setVerifying(false);
        return;
      }
      // MSG91 widget exposeMethods signature: verifyOtp(otp:Number, successCb, failureCb).
      // Do NOT pass reqId here — the widget tracks the current session internally.
      // OTP must be a Number, otherwise MSG91 rejects it.
      const otpNum = Number(otp);
      if (Number.isNaN(otpNum)) {
        setVerifyDebug({ ok: false, payload: { reason: "otp_not_numeric" } });
        toast.error("OTP must be numeric");
        setVerifying(false);
        return;
      }
      console.log("[MSG91] verifyOtp called with OTP:", otpNum);
      window.verifyOtp(
        otpNum,
        async (data) => {
          console.log("[MSG91] verifyOtp SUCCESS payload:", data);
          // MSG91 OTP Widget hands back an access-token on successful OTP verification.
          // Depending on widget version it may be at:
          //   - window.getAccessToken()  (most reliable)
          //   - data.message           (v1 widget doc contract)
          //   - data itself (string)
          const looksLikeJwt = (s) => typeof s === "string" && s.split(".").length >= 3;
          const getAccess = typeof window.getAccessToken === "function" ? window.getAccessToken() : null;
          const candidates = [
            getAccess,
            data?.message,
            typeof data === "string" ? data : null,
            data?.access_token,
            data?.accessToken,
            data?.data?.message,
            data?.data?.access_token,
          ].filter(Boolean);
          // Prefer the first JWT-shaped candidate; fall back to the first candidate.
          const accessToken = candidates.find(looksLikeJwt) || candidates[0] || null;
          console.log(
            "[MSG91] extracted accessToken:",
            accessToken ? String(accessToken).slice(0, 40) + "…" : null,
            "| candidates:",
            candidates.map((c) => (typeof c === "string" ? c.slice(0, 20) + "…" : c))
          );
          if (!accessToken) {
            setVerifyDebug({ ok: false, payload: { reason: "no_access_token_in_success", raw: data } });
            toast.error("MSG91 didn't return an access token");
            setVerifying(false);
            return;
          }
          try {
            const res = await msg91VerifyAccessToken(accessToken, clean, name || "");
            setVerifyDebug({ ok: true, payload: { widget: data, server: { phone: res.phone, name: res.name } } });
            onVerified?.((res.phone || "").replace(/^91/, ""));
            toast.success("Phone verified 🌸");
          } catch (e) {
            const serverErr = e?.response?.data;
            console.error("[MSG91] backend verify failed:", serverErr, "status:", e?.response?.status);
            setVerifyDebug({ ok: false, payload: { serverStatus: e?.response?.status, serverError: serverErr, widgetSuccess: data } });
            toast.error(serverErr?.detail?.detail || serverErr?.detail || "Server rejected the MSG91 token");
          } finally {
            setVerifying(false);
          }
        },
        (err) => {
          console.error("[MSG91] verifyOtp FAILURE payload:", err);
          setVerifyDebug({ ok: false, payload: err });
          toast.error(err?.message || err?.msg || "Invalid OTP — please try again");
          setVerifying(false);
        }
      );
    } catch (e) {
      console.error("doVerify exception:", e);
      setVerifyDebug({ ok: false, payload: String(e) });
      toast.error("Verify failed");
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-2">
      {step === "phone" ? (
        <div className="flex gap-2">
          <div className="flex-1 flex items-center bg-white border border-[#F4D8E2] rounded-xl focus-within:border-[#D81B60] transition-colors">
            <span className="pl-3 text-[#8E6377] text-sm">+91</span>
            <input
              data-testid="checkout-phone"
              type="tel"
              value={phone}
              onChange={e => onPhoneChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="10-digit phone"
              className="flex-1 px-2 py-2 bg-transparent outline-none text-sm"
            />
          </div>
          <button
            type="button"
            data-testid="send-otp-button"
            onClick={doSend}
            disabled={sending || (phone || "").length < 10}
            className="px-4 rounded-xl bg-[#D81B60] hover:bg-[#AD1457] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
          >
            {sending && <Loader2 className="animate-spin" size={12} />} Send OTP
          </button>
        </div>
      ) : (
        <>
          {devOtp && (
            <div data-testid="dev-otp-notice" className="p-2 rounded-lg bg-[#FCE4EC] border border-[#F4D8E2] text-[#AD1457] text-[11px]">
              Dev mode — your OTP: <b className="tracking-widest text-sm">{devOtp}</b>
            </div>
          )}
          <div className="flex gap-2">
            <input
              data-testid="otp-input"
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit OTP"
              inputMode="numeric"
              autoFocus
              className="flex-1 px-3 py-2 text-center tracking-[0.4em] font-bold rounded-xl border border-[#F4D8E2] outline-none focus:border-[#D81B60] text-sm"
            />
            <button
              type="button"
              data-testid="verify-otp-button"
              onClick={doVerify}
              disabled={verifying || otp.length < 4}
              className="px-4 rounded-xl bg-[#D81B60] hover:bg-[#AD1457] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
            >
              {verifying && <Loader2 className="animate-spin" size={12} />} <Check size={12} /> Verify
            </button>
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <button
              type="button"
              data-testid="change-phone-button"
              onClick={() => { setStep("phone"); setOtp(""); setDevOtp(""); setReqId(null); setSendDebug(null); }}
              className="text-[#8E6377] hover:text-[#D81B60]"
            >← Change phone</button>
            <button
              type="button"
              data-testid="resend-otp-button"
              onClick={doResend}
              className="text-[#D81B60] hover:text-[#AD1457] font-semibold"
            >Resend OTP</button>
          </div>
        </>
      )}
    </div>
  );
};
